import { identity } from 'lodash';
import { createStore, applyMiddleware } from 'redux';
import thunk from '@wix/redux-thunk';
import { createRootReducer } from './root-reducer';
import { initBiMiddleware } from './middleware/bi-middleware';
import { createLiveUpdatesMiddleware } from './comments/live-updates/live-updates-middleware';
import { createCommentsService } from '../../controller/services/comments-service';
import { createVotesService } from '../../controller/services/vote-service/create-votes-service';
import { createMembersService } from '../../controller/services/members-service';
import { isMockHttpError } from '../../controller/utils/wrap-with-dev-connection-state';
import { fetchModeMiddleware } from './middleware/fetch-mode-middleware';
export function createCommentsStore({ wixCodeApi, platformAPIs, appDefinitionId, compId, actionPreconditions, isSSR, duplexerClient, sentry, devToolsApi, testMode, instanceManager, commentsWatcherMiddleware, executeBeforeCrud, logNetworkErrorsInConsole, httpClient, errorHandler, mocks, persistDraft, preloadedState, }) {
    // if you need this, just reenable for dev
    /*   const composeEnhancers = require('remote-redux-devtools').composeWithDevTools({
      realtime: true,
      port: 8000,
    }); */
    const composeEnhancers = identity;
    const ssrFetchMode = wixCodeApi.window.warmupData.get('SSR_FETCH_MODE');
    const trackError = (source) => logNetworkErrorsInConsole
        ? (error, args) => console.error(`NETWORK ERROR from ${source}\n`, error, '\nStatus', error?.response?.status, '\nRequestId', error?.response?.requestId, '\nArguments', args)
        : undefined;
    const fedopsLogger = platformAPIs?.fedOpsLoggerFactory?.getLoggerForWidget({
        appId: 'wix-comments-lib',
        widgetId: 'widget',
    });
    const createServiceErrorHandler = (serviceName) => (error, args) => {
        trackError(serviceName)?.(error, args);
        if (!isMockHttpError(error)) {
            sentry.captureException(error, { tags: { capturedBy: serviceName } });
        }
    };
    const commentsService = createCommentsService({
        fedopsLogger,
        httpClient,
        errorHandler,
        getCrudDevState: () => devToolsApi.getState().crudState,
        getFetchDevState: () => devToolsApi.getState().fetchState,
        onError: createServiceErrorHandler('comments-service'),
        mocks,
        persistDraft,
    });
    const middleware = [
        commentsWatcherMiddleware,
        initBiMiddleware(platformAPIs, instanceManager),
        fetchModeMiddleware,
        thunk.withExtraArgument({
            wixCodeApi,
            platformAPIs,
            compId,
            commentsService,
            votesService: createVotesService({
                httpClient,
                errorHandler,
                getDevState: () => devToolsApi.getState().crudState,
                onError: createServiceErrorHandler('votes-service'),
            }),
            membersService: createMembersService({
                httpClient,
                errorHandler,
                getDevState: () => devToolsApi.getState().crudState,
                onError: createServiceErrorHandler('members-service'),
            }),
            actionPreconditions,
            httpClient,
            errorHandler,
            testMode,
            instanceManager,
            executeBeforeCrud,
            appDefinitionId,
        }),
    ];
    if (duplexerClient && !isSSR) {
        middleware.push(createLiveUpdatesMiddleware({
            duplexerClient,
            appDefId: appDefinitionId,
            commentsService,
            onDuplexerError: (e) => {
                trackError('duplexer')?.(e, undefined);
                sentry.captureException(e, { tags: { capturedBy: 'live updates middleware' } });
            },
        }));
    }
    return createStore(createRootReducer({
        initialFetchMode: isSSR ? 'comments_only' : ssrFetchMode ?? 'comments_and_interactions',
    }), preloadedState, composeEnhancers(applyMiddleware(...middleware)));
}
//# sourceMappingURL=create-comments-store.js.map